import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Parse from 'parse';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import { fetchUserConnectors } from '../userconnectors/userconnector.actions';
import { buildOperatingPulse } from '../utilities/genesisPulse';
import './widgets/LifeBot.css';

const sectionSx = {
  p: 2.5,
  borderRadius: 2,
  border: '1px solid #E2E8F0',
  backgroundColor: '#FFFFFF',
};

const IngestModal = ({ chatBot, onIngestSuccess, onBusyChange }) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [urls, setUrls] = useState(['']);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [docxFiles, setDocxFiles] = useState([]);
  const [xlsxFiles, setXlsxFiles] = useState([]);
  const [txtFiles, setTxtFiles] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [rawText, setRawText] = useState('');
  const [initialPrompt, setInitialPrompt] = useState(
    chatBot?.initialPrompt || ''
  );
  const [botName, setBotName] = useState(chatBot?.botName || '');
  const [purpose, setPurpose] = useState(chatBot?.purpose || '');
  const [instructions, setInstructions] = useState(chatBot?.instructions || '');
  const [selectedConnectorKeys, setSelectedConnectorKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingSources, setExistingSources] = useState([]);
  const [progressLog, setProgressLog] = useState([]);

  const reduxConnectors = useSelector((state) => state.userConnector?.data);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchUserConnectors());
  }, [dispatch]);

  useEffect(() => {
    setInitialPrompt(chatBot?.initialPrompt || '');
    setBotName(chatBot?.botName || '');
    setPurpose(chatBot?.purpose || '');
    setInstructions(chatBot?.instructions || '');
  }, [
    chatBot?.initialPrompt,
    chatBot?.botName,
    chatBot?.purpose,
    chatBot?.instructions,
  ]);

  const parseScopeList = (scopeValue) => {
    if (!scopeValue) {
      return [];
    }

    if (Array.isArray(scopeValue)) {
      return scopeValue.filter(Boolean);
    }

    if (typeof scopeValue === 'string') {
      return scopeValue
        .split(scopeValue.includes(',') ? ',' : /\s+/)
        .map((scope) => scope.trim())
        .filter(Boolean);
    }

    return [];
  };

  const normalizeStringArray = (value) => {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .flat(Infinity)
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  };

  const normalizeConnector = (c) => ({
    id:
      c?.id ||
      c?.objectId ||
      c?.key ||
      c?.provider ||
      c?.name ||
      String(Math.random()),
    key: String(
      c?.connectorId ||
        c?.key ||
        c?.provider ||
        c?.slug ||
        c?.id ||
        c?.name ||
        ''
    ).trim(),
    label: c?.name || c?.label || c?.provider || c?.key || 'Connector',
    scopes: parseScopeList(c?.scope || c?.scopes),
  });

  const connectorOptions = useMemo(
    () =>
      (Array.isArray(reduxConnectors) ? reduxConnectors : []).map(
        normalizeConnector
      ),
    [reduxConnectors]
  );

  const initialConnectorKeys = useMemo(() => {
    if (Array.isArray(chatBot?.connectedConnectorKeys)) {
      return normalizeStringArray(chatBot.connectedConnectorKeys);
    }

    if (Array.isArray(chatBot?.connectedConnectors)) {
      return normalizeStringArray(chatBot.connectedConnectors).map(
        (connector) => connector.toLowerCase()
      );
    }

    return [];
  }, [chatBot?.connectedConnectorKeys, chatBot?.connectedConnectors]);

  useEffect(() => {
    setSelectedConnectorKeys(initialConnectorKeys);
  }, [initialConnectorKeys]);

  const selectedConnectorProfiles = useMemo(
    () =>
      connectorOptions.filter((option) => {
        const optionKey = option.key;
        const optionLabel = String(option.label).toLowerCase();
        return (
          selectedConnectorKeys.includes(optionKey) ||
          selectedConnectorKeys.includes(optionLabel)
        );
      }),
    [connectorOptions, selectedConnectorKeys]
  );

  const operatingPulse = useMemo(
    () => buildOperatingPulse(selectedConnectorProfiles),
    [selectedConnectorProfiles]
  );

  useEffect(() => {
    const fetchSources = async () => {
      if (!chatBot?.chatBotId) return;
      try {
        const sources = await Parse.Cloud.run('listSources', {
          chatBotId: chatBot?.chatBotId,
        });
        setExistingSources(sources);
      } catch (err) {
        console.error('Error fetching sources:', err);
      }
    };
    fetchSources();
  }, [chatBot?.chatBotId]);

  const refreshSources = async () => {
    if (!chatBot?.chatBotId) return;
    try {
      const sources = await Parse.Cloud.run('listSources', {
        chatBotId: chatBot?.chatBotId,
      });
      setExistingSources(sources);
    } catch (err) {
      console.error('Error refreshing sources:', err);
    }
  };

  const newSourcesAreAllComplete = () => {
    const expectedSources = progressLog
      .filter((entry) => entry.startsWith('⏳ Processing: '))
      .map((entry) => entry.replace('⏳ Processing: ', '').trim());

    return expectedSources.every((expected) =>
      existingSources.some((src) => src.source.includes(expected))
    );
  };

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(async () => {
      await refreshSources();
      if (newSourcesAreAllComplete()) clearInterval(interval);
    }, 5000);
    return () => clearInterval(interval);
  }, [loading]);

  const detectFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['mp4', 'mov', 'webm'].includes(ext)) return 'video';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext))
      return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    return 'unknown';
  };

  const upload = async (fileList) => {
    const uploaded = [];
    for (const file of fileList) {
      const type = detectFileType(file.name);
      if (process.env.REACT_APP_BACKEND_PROVIDER === 'firebase') {
        const asset = await lifeTaggerApi.uploadAsset(file);
        if (!asset.url)
          throw new Error('The upload did not return a file address.');
        uploaded.push({ name: file.name, url: asset.url, type });
        continue;
      }
      const parseFile = new Parse.File(file.name, file);
      await parseFile.save();
      uploaded.push({ name: file.name, url: parseFile.url(), type });
    }
    return uploaded;
  };

  const removeExistingSource = async (sourceId) => {
    try {
      await Parse.Cloud.run('deleteSource', {
        chatBotId: chatBot?.chatBotId,
        sourceId,
      });
      setExistingSources((prev) =>
        prev.filter((src) => src.sourceId !== sourceId)
      );
    } catch (err) {
      console.error('Failed to delete source:', err);
    }
  };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'pdf') setPdfFiles([...pdfFiles, ...files]);
    else if (type === 'docx') setDocxFiles([...docxFiles, ...files]);
    else if (type === 'txt') setTxtFiles([...txtFiles, ...files]);
    else if (type === 'xlsx') setXlsxFiles([...xlsxFiles, ...files]);
    else if (type === 'media') setMediaFiles([...mediaFiles, ...files]);
  };

  const removeFile = (index, type) => {
    if (type === 'pdf') setPdfFiles(pdfFiles.filter((_, i) => i !== index));
    else if (type === 'docx')
      setDocxFiles(docxFiles.filter((_, i) => i !== index));
    else if (type === 'txt')
      setTxtFiles(txtFiles.filter((_, i) => i !== index));
    else if (type === 'xlsx')
      setXlsxFiles(xlsxFiles.filter((_, i) => i !== index));
    else if (type === 'media')
      setMediaFiles(mediaFiles.filter((_, i) => i !== index));
  };

  const handleUrlChange = (value, index) => {
    const updated = [...urls];
    updated[index] = value;
    setUrls(updated);
  };

  const addUrlField = () => setUrls([...urls, '']);
  const removeUrlField = (index) => {
    const updated = urls.filter((_, i) => i !== index);
    setUrls(updated);
  };

  const handleSubmit = async () => {
    if (!initialPrompt?.trim()) {
      setError('Initial Greeting is required.');
      return;
    }

    setLoading(true);
    onBusyChange?.(true);
    setError('');
    setProgressLog([]);

    try {
      const uploadedPdfs = await upload(pdfFiles);
      const uploadedDocx = await upload(docxFiles);
      const uploadedTxt = await upload(txtFiles);
      const uploadedMedia = await upload(mediaFiles);
      const uploadedXlsx = await upload(xlsxFiles);

      const params = {
        chatBotId: chatBot?.chatBotId || null,
        botName,
        purpose,
        instructions,
        initialPrompt,
        initialQuestions: operatingPulse.questions,
        operatingPulse,
        urls: urls.filter(Boolean),
        files: [
          ...uploadedPdfs,
          ...uploadedDocx,
          ...uploadedTxt,
          ...uploadedXlsx,
        ],
        media: uploadedMedia,
        rawText,
        connectedConnectors: selectedConnectorProfiles.map(
          (connector) => connector.label
        ),
        connectedConnectorKeys: [
          ...new Set(
            selectedConnectorProfiles.map((connector) => connector.key)
          ),
        ],
      };

      [
        ...uploadedPdfs,
        ...uploadedDocx,
        ...uploadedTxt,
        ...uploadedXlsx,
        ...uploadedMedia,
      ].forEach((file) => {
        const name = file.name || file.url || file;
        setProgressLog((log) => [...log, `⏳ Processing: ${name}`]);
      });

      if (onIngestSuccess) await onIngestSuccess(params);

      if (chatBot?.chatBotId) {
        await refreshSources();
        setPdfFiles([]);
        setDocxFiles([]);
        setTxtFiles([]);
        setMediaFiles([]);
        setXlsxFiles([]);
        setRawText('');
        setUrls(['']);
      }

      setSuccessMessage('All sources and connector settings saved.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong.');
      setProgressLog((log) => [...log, `❌ Error: ${err.message}`]);
    } finally {
      setLoading(false);
      onBusyChange?.(false);
    }
  };

  return (
    <Paper
      className="lt-lifebot-ingest"
      sx={{
        maxWidth: 980,
        width: '100%',
        mx: 'auto',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        boxShadow: '0 24px 48px rgba(15, 23, 42, 0.10)',
        overflow: 'hidden',
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          color: '#0F172A',
          background: 'linear-gradient(135deg, #F8FAFC, #EEF2FF)',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sources & instructions
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: '#475569' }}>
          Choose what your LifeBot knows and which connected tools it can use.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#F8FAFC', p: 3 }}>
        <Stack spacing={2.5}>
          <Box sx={sectionSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Bot Identity
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Name your bot and set the exact first message users will see.
            </Typography>
            <Stack spacing={2}>
              <Tooltip title="This is how your chatbot will be identified by users.">
                <TextField
                  label="Chatbot Name"
                  fullWidth
                  variant="outlined"
                  value={botName}
                  onChange={(e) => setBotName(e.target.value)}
                />
              </Tooltip>
              <TextField
                label="What should this LifeBot help people do?"
                fullWidth
                multiline
                minRows={2}
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
              />
              <TextField
                label="Guidance for your LifeBot"
                helperText="Describe its approach, preferred sources, and boundaries. Account permissions still apply."
                fullWidth
                multiline
                minRows={3}
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
              />
              <Tooltip title="Initial greeting is authored by you.">
                <TextField
                  label="Initial Greeting"
                  fullWidth
                  required
                  variant="outlined"
                  value={initialPrompt}
                  onChange={(e) => setInitialPrompt(e.target.value)}
                  placeholder="Welcome to Genesis. What would you like to focus on first?"
                />
              </Tooltip>
            </Stack>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Connected Data Sources
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Select which connected systems this instance can use.
            </Typography>
            <Autocomplete
              multiple
              options={connectorOptions}
              disableCloseOnSelect
              getOptionLabel={(opt) => opt.label}
              value={connectorOptions.filter(
                (o) =>
                  selectedConnectorKeys.includes(o.key) ||
                  selectedConnectorKeys.includes(String(o.label).toLowerCase())
              )}
              onChange={(_, newVals) =>
                setSelectedConnectorKeys(newVals.map((v) => v.key))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  <span style={{ fontWeight: 600 }}>{option.label}</span>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={opt.id}
                    label={opt.label}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select connectors"
                  placeholder="Search connectors"
                />
              )}
            />
          </Box>

          <Box
            component="details"
            sx={{
              ...sectionSx,
              border: '1px solid #DCE7FF',
              background:
                'linear-gradient(180deg, rgba(239,246,255,0.85) 0%, rgba(255,255,255,1) 100%)',
            }}
          >
            <Typography
              component="summary"
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5, cursor: 'pointer' }}
            >
              Suggested conversation starters
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              A few ways to begin exploring your connected sources.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip
                label={`${operatingPulse.connectors.length} connectors`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${operatingPulse.scopeCount} scopes`}
                color="secondary"
                variant="outlined"
              />
            </Box>
            <List dense sx={{ pt: 0 }}>
              {operatingPulse.questions.map((question) => (
                <ListItem key={question} sx={{ px: 0 }}>
                  <ListItemText primary={question} />
                </ListItem>
              ))}
            </List>
          </Box>

          {existingSources.length > 0 && (
            <Box sx={sectionSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Existing Sources
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
                Sources currently embedded in this chatbot.
              </Typography>

              {progressLog.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Ingestion Progress
                  </Typography>
                  <List dense>
                    {progressLog.map((entry, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={entry} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <List dense>
                {existingSources.map((src, i) => (
                  <ListItem
                    key={i}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => removeExistingSource(src.sourceId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={src.source}
                      secondary={`ID: ${src.sourceId}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={sectionSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Website URLs
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Add pages with relevant information for this bot.
            </Typography>
            <Stack spacing={1}>
              {urls.map((u, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label={`Website URL ${i + 1}`}
                    value={u}
                    onChange={(e) => handleUrlChange(e.target.value, i)}
                  />
                  <IconButton onClick={() => removeUrlField(i)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button onClick={addUrlField} variant="text" sx={{ mt: 1 }}>
              Add Another URL
            </Button>
          </Box>

          {[
            ['PDF Files', 'pdf', pdfFiles],
            ['DOCX Files', 'docx', docxFiles],
            ['XLSX Files', 'xlsx', xlsxFiles],
            ['TXT Files', 'txt', txtFiles],
            ['Media Files', 'media', mediaFiles],
          ].map(([label, type, files]) => (
            <Box key={type} sx={sectionSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
                Upload one or more files.
              </Typography>
              <Button variant="contained" component="label">
                Upload {label}
                <input
                  type="file"
                  accept={type === 'media' ? 'image/*,video/*' : `.${type}`}
                  hidden
                  multiple
                  onChange={(e) => handleFileChange(e, type)}
                />
              </Button>
              <List dense sx={{ mt: 1 }}>
                {files.map((file, i) => (
                  <ListItem
                    key={i}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(i, type)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={file.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}

          <Box sx={sectionSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Pasted Text
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              Optional freeform notes and instructions.
            </Typography>
            <TextField
              label="Your Text Content"
              multiline
              minRows={5}
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </Box>

          {error && <FormHelperText error>{error}</FormHelperText>}
        </Stack>
      </DialogContent>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />

      <Box
        sx={{
          px: 3,
          py: 2,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ fontWeight: 'bold', minWidth: 140 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Save Sources'}
        </Button>
      </Box>
    </Paper>
  );
};

export default IngestModal;
